import { ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useDeleteMarket, useMarketAvailableMethods } from '@/api/domains/markets';
import type { Market } from '@/api/types/services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { CountryFlags } from './components/CountryFlags';
import { MarketMethodList } from './MarketMethodList';

/**
 * One market between #ededed hairlines (Figma - no card chrome): name + flag row on
 * the left; dashed-trash / solid-pencil / chevron controls on the right; the method
 * list below while expanded.
 */
export function MarketSection({
  market,
  onEdit,
}: {
  market: Market;
  onEdit: (market: Market) => void;
}) {
  const { t } = useTranslation('markets');
  const tCommon = useTranslation('common').t;
  const [open, setOpen] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const methodsQuery = useMarketAvailableMethods(market.id, { enabled: open });
  const deleteMutation = useDeleteMarket();

  const handleDelete = () => {
    deleteMutation.mutate(market.id, {
      onSuccess: () => toast.success(t('marketDeleted')),
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  return (
    <section>
      <div className="border-t border-[#ededed]" />
      <div className="flex items-center justify-between py-4">
        <div className="flex min-w-0 flex-col gap-[3px]">
          <h3 className="truncate text-xl font-medium tracking-[-0.6px] text-black">
            {market.name}
          </h3>
          <CountryFlags codes={market.countries} />
        </div>
        <div className="flex shrink-0 items-center gap-3.5">
          <button
            type="button"
            aria-label={t('actions.deleteMarket')}
            onClick={() => setConfirmDelete(true)}
            className="flex size-9 items-center justify-center rounded-md border border-dashed border-black bg-[#f2f2f2] transition-colors hover:bg-muted"
          >
            <Trash2 className="size-5" />
          </button>
          <button
            type="button"
            aria-label={t('actions.editMarket')}
            onClick={() => onEdit(market)}
            className="flex size-9 items-center justify-center rounded-md border border-black bg-background transition-colors hover:bg-accent"
          >
            <Pencil className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t('actions.collapse') : t('actions.expand')}
            className="flex h-9 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent"
          >
            <ChevronUp className={cn('size-6 transition-transform', !open && 'rotate-180')} />
          </button>
        </div>
      </div>
      <div className="border-t border-[#ededed]" />

      {open ? (
        <div className="pt-3">
          {methodsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : methodsQuery.isError ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed p-4">
              <p className="text-sm text-muted-foreground">{t('methods.loadError')}</p>
              <Button variant="outline" size="sm" onClick={() => void methodsQuery.refetch()}>
                {tCommon('actions.retry')}
              </Button>
            </div>
          ) : (
            <MarketMethodList market={market} methods={methodsQuery.data ?? []} />
          )}
        </div>
      ) : null}
      <div className="pb-3" />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('confirmDeleteMarket.title', { name: market.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteMarket.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
